import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../common/api/request';
import { UserPreference, SaveUserPreferenceItem } from '../types/userPreferences';
import { effectiveOperatorsKeys } from './useEffectiveOperators';

// ── Query keys ───────────────────────────────────────────────────────────────

export const userPreferencesKeys = {
  /** Root key — invalidating this clears all user-preference queries. */
  all: ['user-preferences'] as const,
  /** `GET /user-preferences` — all preferences for the logged-in user. */
  list: () => [...userPreferencesKeys.all, 'list'] as const,
  /** `GET /user-preferences/{year}` — preferences for a specific year. */
  byYear: (year: number) => [...userPreferencesKeys.all, 'year', year] as const,
};

// ── Queries ──────────────────────────────────────────────────────────────────

/**
 * Fetch **all** saved preferences for the logged-in user.
 *
 * Endpoint: `GET /user-preferences`
 */
export function useUserPreferences() {
  const { data, isLoading, error, refetch } = useQuery<UserPreference[]>({
    queryKey: userPreferencesKeys.list(),
    queryFn: async () => {
      const resp = await axios.get<UserPreference[]>('/user-preferences');
      return resp.data;
    },
  });

  return {
    preferences: data ?? [],
    isLoading,
    error,
    refetch,
  };
}

/**
 * Fetch saved preferences for a specific accounting year.
 *
 * Endpoint: `GET /user-preferences/{year}`
 *
 * @param year    Accounting year to fetch preferences for.
 * @param enabled When `false` the query is disabled (no request is made).
 */
export function useUserPreferencesByYear(year: number, enabled = true) {
  const shouldFetch = enabled && year > 0;

  const { data, isLoading, error, refetch } = useQuery<UserPreference[]>({
    queryKey: userPreferencesKeys.byYear(year),
    queryFn: async () => {
      const resp = await axios.get<UserPreference[]>(`/user-preferences/${year}`);
      return resp.data;
    },
    enabled: shouldFetch,
  });

  return {
    preferences: data ?? [],
    isLoading,
    error,
    refetch,
  };
}

// ── Mutations ────────────────────────────────────────────────────────────────

export interface UseSaveUserPreferencesOptions {
  onSuccess?: (data: UserPreference[]) => void;
  onError?: (error: Error) => void;
}

/**
 * Bulk save / update user preferences.
 *
 * Endpoint: `POST /user-preferences/bulk`
 *
 * On success the **entire** user-preferences cache is invalidated so that
 * subsequent queries (list, by-year) return fresh data.
 */
export function useSaveUserPreferences(options?: UseSaveUserPreferencesOptions) {
  const queryClient = useQueryClient();

  return useMutation<UserPreference[], Error, SaveUserPreferenceItem[]>({
    mutationFn: async (items) => {
      const resp = await axios.post<UserPreference[]>('/user-preferences/bulk', items);
      return resp.data;
    },
    onSuccess: (data) => {
      // Invalidate all user-preference queries so lists / by-year refetch.
      queryClient.invalidateQueries({ queryKey: userPreferencesKeys.all });
      // Also invalidate effective-operators so module pages reflect the change.
      queryClient.invalidateQueries({ queryKey: effectiveOperatorsKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('Failed to save user preferences:', error);
      options?.onError?.(error);
    },
  });
}

